import React, { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { ValueStepper } from '../../modules/Onboarding/components/ValueStepper';
import { GlassSurface } from '../ui/glass';
import { GlassButton } from '../ui/GlassButton';

export type PortfolioAccountDraft = {
  label: string;
  balance: number;
  expectedReturnPct?: number;
  hiddenFromHousehold?: boolean;
};

type Props = {
  visible: boolean;
  initial: PortfolioAccountDraft | null;
  mode?: 'edit' | 'add';
  category?: 'investment' | 'other';
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: PortfolioAccountDraft) => void;
};

const BALANCE_STEP = 100;

/** Web centered dialog to add/edit a synced portfolio row. */
export function EditPortfolioAccountModal({
  visible,
  initial,
  mode = 'edit',
  category = 'investment',
  saving = false,
  onClose,
  onSave,
}: Props) {
  const [label, setLabel] = useState('');
  const [balance, setBalance] = useState(0);
  const [returnPct, setReturnPct] = useState('');
  const [hiddenFromHousehold, setHiddenFromHousehold] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAdd = mode === 'add';
  const isOther = category === 'other';

  useEffect(() => {
    if (!visible) return;
    setLabel(initial?.label ?? '');
    setBalance(Math.max(0, initial?.balance ?? 0));
    setReturnPct(
      initial?.expectedReturnPct != null && Number.isFinite(initial.expectedReturnPct)
        ? String(initial.expectedReturnPct)
        : '',
    );
    setHiddenFromHousehold(initial?.hiddenFromHousehold === true);
    setError(null);
    // Snapshot when the sheet opens or switches add/edit — `initial` is a new
    // object every parent render and must not wipe in-progress typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [visible, mode]);

  const handleSave = () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Enter a name.');
      return;
    }
    if (balance < 0) {
      setError('Balance cannot be negative.');
      return;
    }
    let expectedReturnPct: number | undefined;
    if (!isOther && returnPct.trim() !== '') {
      const n = Number.parseFloat(returnPct.replace(/[^0-9.\-]/g, ''));
      if (!Number.isFinite(n)) {
        setError('Return % looks invalid.');
        return;
      }
      expectedReturnPct = n;
    }
    onSave({
      label: trimmed,
      balance,
      expectedReturnPct,
      hiddenFromHousehold,
    });
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.dismissArea}
          onPress={onClose}
          accessibilityLabel="Dismiss"
        />
        <View style={styles.panelWrap} pointerEvents="box-none">
          <GlassSurface
            tint="neutral"
            rounded="xl"
            showHairline
            style={styles.panel}
            contentStyle={styles.panelContent}
          >
            <Text style={styles.title}>
              {isAdd
                ? isOther
                  ? 'Add other asset'
                  : 'Add account'
                : isOther
                  ? 'Edit other asset'
                  : 'Edit account'}
            </Text>
            <Text style={styles.sub}>
              {isOther
                ? 'Home, cash, or other non-invested assets. Syncs across devices.'
                : 'Manual account — edits sync across devices and Trends.'}
            </Text>

            <TextInput
              value={label}
              onChangeText={setLabel}
              placeholder={isOther ? 'Name' : 'Account name'}
              placeholderTextColor={colors.textMuted}
              style={styles.input}
              autoCapitalize="words"
              autoFocus={isAdd}
              returnKeyType="done"
              accessibilityLabel="Name"
            />

            <ValueStepper
              label="Balance"
              value={balance}
              prefix="$"
              min={0}
              decimals
              onChange={(v) => setBalance(Math.max(0, v))}
              onDecrease={() =>
                setBalance((v) => Math.max(0, Math.round((v - BALANCE_STEP) * 100) / 100))
              }
              onIncrease={() =>
                setBalance((v) => Math.round((v + BALANCE_STEP) * 100) / 100)
              }
            />

            {!isOther ? (
              <TextInput
                value={returnPct}
                onChangeText={setReturnPct}
                placeholder="Return % (optional)"
                placeholderTextColor={colors.textMuted}
                keyboardType="decimal-pad"
                style={styles.input}
                accessibilityLabel="Expected return percent"
              />
            ) : null}

            {!isAdd ? (
              <Pressable
                onPress={() => setHiddenFromHousehold((v) => !v)}
                style={styles.hideRow}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: hiddenFromHousehold }}
              >
                <View style={[styles.checkbox, hiddenFromHousehold && styles.checkboxOn]}>
                  {hiddenFromHousehold ? <Text style={styles.checkboxMark}>✓</Text> : null}
                </View>
                <View style={styles.hideCopy}>
                  <Text style={styles.hideLabel}>Personal only</Text>
                  <Text style={styles.hideHint}>Hidden from household view</Text>
                </View>
              </Pressable>
            ) : null}

            {error ? <Text style={styles.error}>{error}</Text> : null}

            <View style={styles.actions}>
              <GlassButton variant="ghost" size="md" onPress={onClose} disabled={saving}>
                Cancel
              </GlassButton>
              <GlassButton
                variant="primary"
                size="md"
                onPress={handleSave}
                disabled={saving}
                accessibilityLabel={isAdd ? 'Add' : 'Save'}
              >
                {saving ? 'Saving…' : isAdd ? 'Add' : 'Save'}
              </GlassButton>
            </View>
          </GlassSurface>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  dismissArea: {
    ...StyleSheet.absoluteFillObject,
  },
  panelWrap: {
    width: '100%',
    maxWidth: 420,
    zIndex: 1,
  },
  panel: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
    borderRadius: radii.xl,
  },
  panelContent: {
    gap: spacing.sm,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  sub: {
    ...typography.body,
    color: colors.textMuted,
    marginBottom: spacing.xs,
  },
  input: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
    borderRadius: radii.md,
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    color: colors.text,
    ...typography.body,
    backgroundColor: colors.surface,
  },
  error: {
    ...typography.caption,
    color: colors.danger,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  hideRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOn: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  checkboxMark: {
    color: colors.background,
    fontSize: 12,
    fontWeight: '800',
    lineHeight: 14,
  },
  hideCopy: {
    flex: 1,
    gap: 2,
  },
  hideLabel: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  hideHint: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 12,
  },
});
